import React, { useCallback, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CardPingContext } from '../../../contexts/CardPingContext';
import type { Placement, WallGeometry } from '../../../utils/orbitLayout';
interface OrbitSlotProps {
  uid: string;
  p: Placement;
  g: WallGeometry;
  /** Measured card height (unscaled) */
  h: number;
  reduce: boolean;
  onMeasure: (uid: string, h: number) => void;
  onPing: (uid: string) => void;
  children: React.ReactNode;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** Positions one card in orbit. It emerges from Anna's core along its filament and retracts into it. */
export function OrbitSlot({
  uid,
  p,
  g,
  h,
  reduce,
  onMeasure,
  onPing,
  children
}: OrbitSlotProps) {
  const inner = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = inner.current;
    if (!el) return;
    onMeasure(uid, el.offsetHeight);
    const ro = new ResizeObserver(() => onMeasure(uid, el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [uid, onMeasure]);
  const ping = useCallback(() => onPing(uid), [onPing, uid]);

  // The box is scaled around its inner edge, so pin that edge exactly on the filament anchor.
  const x = p.side === 'links' ? g.cx + p.anchorX - g.cardW : g.cx + p.anchorX;
  const y = g.cy + p.y - h / 2;
  const core = {
    x: g.cx - g.cardW / 2,
    y: g.cy - h / 2
  };
  const tilt = p.side === 'links' ? 6 : -6;
  return <motion.div className="pointer-events-auto absolute left-0 top-0" style={{
    width: g.cardW,
    zIndex: 20 - p.depth,
    transformOrigin: p.side === 'links' ? '100% 50%' : '0% 50%'
  }} initial={reduce ? {
    opacity: 0,
    x,
    y,
    scale: p.scale
  } : {
    opacity: 0,
    x: core.x,
    y: core.y,
    scale: 0.16,
    rotateY: 0
  }} animate={{
    opacity: 1,
    x,
    y,
    scale: p.scale,
    rotateY: reduce ? 0 : tilt
  }} exit={reduce ? {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  } : {
    opacity: 0,
    x: core.x,
    y: core.y,
    scale: 0.16,
    rotateY: 0,
    transition: {
      duration: 0.28,
      ease: EASE
    }
  }} transition={reduce ? {
    duration: 0.2
  } : {
    type: 'spring',
    stiffness: 250,
    damping: 30,
    mass: 0.9,
    opacity: {
      duration: 0.2
    }
  }} data-id="element-1675" data-mp="2t7mekh0eefr7">
      <div ref={inner} data-id="element-1676" data-mp="3gtos7otpy5on">
        <CardPingContext.Provider value={ping} data-id="element-1677" data-mp="3gsx38wumn67c">{children}</CardPingContext.Provider>
      </div>
    </motion.div>;
}